import Typography from '@mui/material/Typography';
import { useLoginWithApple, useSignup } from 'features/user';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ThirdPartySignupButtons } from 'shared/components/connected/third-party-signup-buttons';
import { LoginLogo } from 'shared/components/ui/login-logo';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { Paths } from 'shared/routes';
import { PageEvents } from 'shared/services/tracking';

import { SignupForm } from '../signup-form';
import * as Styled from './create-account.style';

export const CreateAccount: React.FC = () => {
  useTrackPageVisitEvent(PageEvents.Signup, undefined);
  const { t } = useTranslation();
  const locale = useI18nLocale();

  const {
    signup: signupMailPassword,
    isLoading: isLoadingMailPassword,
    error: errorMailPassword,
  } = useSignup();
  const { login: loginApple, isLoading: isLoadingApple } = useLoginWithApple();

  const onSignupWithApple = () => loginApple();

  const onSignupWithMailPassword = ({
    name,
    email,
    password,
  }: {
    name: string;
    email: string;
    password: string;
  }) =>
    signupMailPassword({
      name,
      email,
      password,
      locale,
    });

  const isLoading = isLoadingMailPassword || isLoadingApple;

  return (
    <Styled.Container>
      <Styled.Content>
        <Styled.Body>
          <Styled.BodyContent>
            <LoginLogo />

            <Typography variant="h1" textAlign="center">
              {t('forms.signup.title')}
            </Typography>

            <ThirdPartySignupButtons
              isLoading={isLoading}
              onAppleLogin={onSignupWithApple}
            />

            <Styled.Divider />

            <SignupForm
              onSubmit={onSignupWithMailPassword}
              isLoading={isLoading}
              error={errorMailPassword ?? undefined}
            />
          </Styled.BodyContent>
        </Styled.Body>
        <Styled.Footer>
          <Typography variant="overline" component="span" textAlign="center">
            {t('forms.signup.has-account.question.label')}{' '}
            <Styled.Anchor to={Paths.Login}>
              {t('forms.signup.has-account.link.label')}
            </Styled.Anchor>
          </Typography>

          <Styled.TermsOfUse>
            {t('forms.terms-and-agreement.1')}
            <Styled.ExternalAnchor
              href="https://griply.app/eula"
              target="_blank"
            >
              {t('forms.terms-and-agreement.terms-of-use')}
            </Styled.ExternalAnchor>
            {t('forms.terms-and-agreement.2')}
            <Styled.ExternalAnchor
              href="https://griply.app/privacy-policy"
              target="_blank"
            >
              {t('forms.terms-and-agreement.privacy-policy')}
            </Styled.ExternalAnchor>
            {t('forms.terms-and-agreement.3')}
          </Styled.TermsOfUse>
        </Styled.Footer>
      </Styled.Content>

      <Styled.IllustrationContainer>
        <Styled.Illustration
          src="/images/signup/illustration.png"
          srcSet="/images/signup/illustration@2x.png 2x, /images/signup/illustration@3x.png 3x"
        />
      </Styled.IllustrationContainer>
    </Styled.Container>
  );
};
